/**
 * Hook for downloading and decrypting E2E media attachments.
 *
 * Given an attachment from a decrypted message payload, this hook:
 * 1. Requests a gated presigned download URL from the API
 * 2. Polls if the scan is still pending (moderation not yet resolved)
 * 3. Fetches the encrypted blob from S3
 * 4. Decrypts using the per-attachment symmetric key + nonce
 * 5. Produces a blob URL for rendering
 *
 * Decrypted blob URLs are cached at the module level so that message list
 * mount/unmount cycles do not trigger repeated downloads. An entry is pinned
 * while any consumer that is not skipping has it mounted; unpinned entries are
 * evicted least-recently-used once the cache exceeds its byte or entry budget.
 * `clearMediaCache()` drops everything, e.g. on logout.
 *
 * SECURITY: The encryptionKey and encryptionNonce live exclusively inside
 * the E2E-encrypted message payload — the server never sees them.
 */

import { useState, useEffect, useMemo, useCallback, useSyncExternalStore } from 'react';
import {
  createApiClient,
  mapModerationReasonToUserMessage,
  type ModerationSkipReason,
} from '@adieuu/shared';
import { decrypt as decryptSymmetric, fromBase64 } from '@adieuu/crypto';
import { useAppConfig } from '../config';
import type { MediaAttachment } from '../services/messagePayload';
import type { MediaMessageState } from '../components/MediaMessage';

const POLL_INTERVAL_MS = 2500;
const MAX_POLL_ATTEMPTS = 72; // ~3 minutes

const DEFAULT_CACHE_LIMITS = { maxBytes: 128 * 1024 * 1024, maxEntries: 400 };
let cacheLimits = { ...DEFAULT_CACHE_LIMITS };

type ApiClient = ReturnType<typeof createApiClient>;

// ---------------------------------------------------------------------------
// Module-level cache: survives list remount cycles.
// Keyed by e2eMediaId. Map insertion order doubles as LRU order (oldest first).
// ---------------------------------------------------------------------------

interface CachedMedia {
  url: string;
  state: MediaMessageState;
  rejectionReason?: string;
  bytes: number;
}

const mediaCache = new Map<string, CachedMedia>();
let cachedBytes = 0;

/** Mounted, non-skipping consumers per media id. Pinned entries are never evicted by the LRU. */
const pinCounts = new Map<string, number>();

/**
 * Bumped by {@link clearMediaCache}. A download started under an older generation
 * must not insert into the cache, so nothing decrypted before a clear outlives it.
 */
let cacheGeneration = 0;

let sweepTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Whether the server reported each media id as never scanned.
 *
 * Kept apart from {@link mediaCache} because a withheld attachment has no decrypted blob to
 * cache, and without this every remount of a hidden attachment would presign again.
 */
const unscannedVerdicts = new Map<string, boolean>();

type DownloadOutcome =
  | { kind: 'cached'; entry: CachedMedia }
  | { kind: 'error'; message: string }
  | { kind: 'withheld' }
  | { kind: 'aborted' };

type DownloadEvent = { type: 'scanning' } | { type: 'verdict'; unscanned: boolean };

/**
 * One download shared by every consumer of the same media id and withholding mode.
 *
 * The job owns its abort signal. It is aborted only once no consumer is waiting, and that abort
 * is deferred a macrotask so an immediate remount (StrictMode, a list re-key) rejoins instead of
 * inheriting a cancelled download.
 */
interface DownloadJob {
  promise: Promise<DownloadOutcome>;
  controller: AbortController;
  waiters: number;
  abortTimer: ReturnType<typeof setTimeout> | null;
  scanning: boolean;
  listeners: Set<(event: DownloadEvent) => void>;
}

const inflightDownloads = new Map<string, DownloadJob>();

/**
 * Keyed by withholding mode as well as media id.
 *
 * A withheld run resolves without fetching bytes, so sharing it with a consumer that may
 * download would leave that consumer loading forever — and sharing in the other direction
 * would hand a withholding consumer a decrypted blob without its own scan verdict.
 */
function inflightKey(mediaId: string, withhold: boolean): string {
  return `${mediaId}:${withhold ? 'withheld' : 'full'}`;
}

// ---------------------------------------------------------------------------
// Usage stats, for the settings UI.
// ---------------------------------------------------------------------------

export interface MediaCacheStats {
  /** Bytes of decrypted media held, including media currently on screen. */
  usedBytes: number;
  /** Budget unpinned media is evicted down to. Pinned media can push usage past it. */
  maxBytes: number;
  entries: number;
  /** Bytes and entries pinned by a mounted consumer; clearing leaves these in place. */
  inUseBytes: number;
  inUseEntries: number;
}

let statsSnapshot: MediaCacheStats | null = null;
const statsListeners = new Set<() => void>();

function notifyStats(): void {
  statsSnapshot = null;
  for (const listener of statsListeners) listener();
}

export function getMediaCacheStats(): MediaCacheStats {
  if (!statsSnapshot) {
    let inUseBytes = 0;
    let inUseEntries = 0;
    for (const [mediaId, entry] of mediaCache) {
      if (!pinCounts.has(mediaId)) continue;
      inUseBytes += entry.bytes;
      inUseEntries += 1;
    }
    statsSnapshot = {
      usedBytes: cachedBytes,
      maxBytes: cacheLimits.maxBytes,
      entries: mediaCache.size,
      inUseBytes,
      inUseEntries,
    };
  }
  return statsSnapshot;
}

export function subscribeMediaCacheStats(listener: () => void): () => void {
  statsListeners.add(listener);
  return () => {
    statsListeners.delete(listener);
  };
}

export function useMediaCacheStats(): MediaCacheStats {
  return useSyncExternalStore(subscribeMediaCacheStats, getMediaCacheStats, getMediaCacheStats);
}

/**
 * Evict every entry no mounted consumer is showing. Media on screen stays, since revoking it
 * would leave that consumer rendering a dead URL.
 */
export function clearUnusedMediaCache(): { freedBytes: number; freedEntries: number } {
  let freedBytes = 0;
  let freedEntries = 0;
  for (const [mediaId, entry] of mediaCache) {
    if (pinCounts.has(mediaId)) continue;
    freedBytes += entry.bytes;
    freedEntries += 1;
    removeEntry(mediaId, entry);
    unscannedVerdicts.delete(mediaId);
  }
  if (freedEntries > 0) notifyStats();
  return { freedBytes, freedEntries };
}

function insertEntry(mediaId: string, entry: CachedMedia): void {
  const existing = mediaCache.get(mediaId);
  if (existing) removeEntry(mediaId, existing);
  mediaCache.set(mediaId, entry);
  cachedBytes += entry.bytes;
  notifyStats();
  scheduleSweep();
}

/** Callers notify stats listeners once they are done removing. */
function removeEntry(mediaId: string, entry: CachedMedia): void {
  if (entry.url) URL.revokeObjectURL(entry.url);
  mediaCache.delete(mediaId);
  cachedBytes -= entry.bytes;
}

function touchEntry(mediaId: string, entry: CachedMedia): void {
  mediaCache.delete(mediaId);
  mediaCache.set(mediaId, entry);
}

function scheduleSweep(): void {
  if (sweepTimer !== null) return;
  sweepTimer = setTimeout(sweepCache, 0);
}

function sweepCache(): void {
  sweepTimer = null;
  const overBudget = () =>
    cachedBytes > cacheLimits.maxBytes || mediaCache.size > cacheLimits.maxEntries;
  if (!overBudget()) return;
  let removed = false;
  for (const [mediaId, entry] of mediaCache) {
    if (!overBudget()) break;
    if (pinCounts.has(mediaId)) continue;
    removeEntry(mediaId, entry);
    unscannedVerdicts.delete(mediaId);
    removed = true;
  }
  if (removed) notifyStats();
}

function pinMedia(mediaId: string): void {
  const count = pinCounts.get(mediaId) ?? 0;
  pinCounts.set(mediaId, count + 1);
  if (count === 0 && mediaCache.has(mediaId)) notifyStats();
}

function unpinMedia(mediaId: string): void {
  const next = (pinCounts.get(mediaId) ?? 0) - 1;
  if (next > 0) {
    pinCounts.set(mediaId, next);
    return;
  }
  pinCounts.delete(mediaId);
  if (mediaCache.has(mediaId)) notifyStats();
  scheduleSweep();
}

function joinDownloadJob(key: string, start: () => DownloadJob): DownloadJob {
  let job = inflightDownloads.get(key);
  if (!job) {
    job = start();
    inflightDownloads.set(key, job);
    const started = job;
    void started.promise.finally(() => {
      if (started.abortTimer !== null) clearTimeout(started.abortTimer);
      started.abortTimer = null;
      if (inflightDownloads.get(key) === started) inflightDownloads.delete(key);
    });
  }
  if (job.abortTimer !== null) {
    clearTimeout(job.abortTimer);
    job.abortTimer = null;
  }
  job.waiters += 1;
  return job;
}

function leaveDownloadJob(key: string, job: DownloadJob): void {
  job.waiters -= 1;
  if (job.waiters > 0 || job.abortTimer !== null) return;
  if (inflightDownloads.get(key) !== job) return;
  job.abortTimer = setTimeout(() => {
    job.abortTimer = null;
    if (job.waiters > 0) return;
    job.controller.abort();
    if (inflightDownloads.get(key) === job) inflightDownloads.delete(key);
  }, 0);
}

export function clearMediaCache(): void {
  cacheGeneration += 1;
  for (const job of inflightDownloads.values()) {
    if (job.abortTimer !== null) clearTimeout(job.abortTimer);
    job.abortTimer = null;
    job.controller.abort();
  }
  inflightDownloads.clear();
  for (const entry of mediaCache.values()) {
    if (entry.url) URL.revokeObjectURL(entry.url);
  }
  mediaCache.clear();
  cachedBytes = 0;
  unscannedVerdicts.clear();
  notifyStats();
}

/** Override the LRU budget in tests; call with no argument to restore the defaults. */
export function __setMediaCacheLimitsForTests(limits?: Partial<typeof DEFAULT_CACHE_LIMITS>): void {
  cacheLimits = { ...DEFAULT_CACHE_LIMITS, ...limits };
  notifyStats();
}

/**
 * True when the server never scanned this media and either side of the transfer describes it as
 * visual. `unsupported_media_type` alone means it was never a scan candidate — a PDF is always
 * recorded as skipped, and warning about it would train people to ignore the warning.
 *
 * Both signals are consulted because both content types are declared by the sender, on separate
 * channels: the skip reason follows the type declared to the server at upload time, while
 * `contentType` here comes from the encrypted payload the recipient renders from. Relabelling
 * either one alone would otherwise hide the warning on media that was never scanned.
 */
function isNeverScanned(
  moderationStatus: string | undefined,
  skipReason: ModerationSkipReason | undefined,
  contentType: string,
): boolean {
  if (moderationStatus !== 'skipped') return false;
  if (skipReason && skipReason !== 'unsupported_media_type') return true;
  return contentType.startsWith('image/') || contentType.startsWith('video/');
}

/**
 * Drop one media id from the cache and revoke its object URL. Used when a
 * Space replaces its logo/banner: the old blob will never be shown again.
 */
export function evictE2EMediaCacheEntry(e2eMediaId: string): void {
  const entry = mediaCache.get(e2eMediaId);
  if (entry) {
    removeEntry(e2eMediaId, entry);
    notifyStats();
  }
  inflightDownloads.delete(inflightKey(e2eMediaId, true));
  inflightDownloads.delete(inflightKey(e2eMediaId, false));
  unscannedVerdicts.delete(e2eMediaId);
}

/** Decrypted object URL for context-menu copy/save, when available. */
export function getE2eDecryptedObjectUrlIfAvailable(e2eMediaId: string): string | null {
  const c = mediaCache.get(e2eMediaId);
  if (c?.state === 'available' && c.url) return c.url;
  return null;
}

// ---------------------------------------------------------------------------
// Download job
// ---------------------------------------------------------------------------

function rejectionReasonFrom(error: { message?: string; details?: Record<string, unknown> } | undefined): string | undefined {
  const rawReason = error?.details?.moderationReason ?? error?.message;
  return (
    mapModerationReasonToUserMessage(typeof rawReason === 'string' ? rawReason : undefined) ??
    (typeof rawReason === 'string' ? rawReason : undefined)
  );
}

function startDownloadJob(
  api: ApiClient,
  mediaId: string,
  attachment: Pick<MediaAttachment, 'encryptionKey' | 'encryptionNonce' | 'contentType'>,
  withholdUnscanned: boolean,
): DownloadJob {
  const controller = new AbortController();
  const signal = controller.signal;
  const generation = cacheGeneration;
  const listeners = new Set<(event: DownloadEvent) => void>();

  const job: DownloadJob = {
    promise: Promise.resolve({ kind: 'aborted' }),
    controller,
    waiters: 0,
    abortTimer: null,
    scanning: false,
    listeners,
  };

  function emit(event: DownloadEvent): void {
    for (const listener of listeners) listener(event);
  }

  /** Record the scan verdict from a presign response; true when the bytes must be withheld. */
  function recordVerdict(data: {
    moderationStatus?: string;
    moderationSkipReason?: ModerationSkipReason;
  }): boolean {
    const verdict = isNeverScanned(
      data.moderationStatus,
      data.moderationSkipReason,
      attachment.contentType,
    );
    unscannedVerdicts.set(mediaId, verdict);
    emit({ type: 'verdict', unscanned: verdict });
    return verdict && withholdUnscanned;
  }

  /** Cache a terminal entry unless a clear or abort happened since the job started. */
  function settleCached(entry: CachedMedia): DownloadOutcome {
    if (signal.aborted || generation !== cacheGeneration) {
      if (entry.url) URL.revokeObjectURL(entry.url);
      return { kind: 'aborted' };
    }
    insertEntry(mediaId, entry);
    return { kind: 'cached', entry };
  }

  function rejected(error: Parameters<typeof rejectionReasonFrom>[0]): DownloadOutcome {
    return settleCached({ url: '', state: 'rejected', rejectionReason: rejectionReasonFrom(error), bytes: 0 });
  }

  type PollResult =
    | { kind: 'url'; downloadUrl: string }
    | { kind: 'done'; outcome: DownloadOutcome };

  async function pollUntilAvailable(): Promise<PollResult> {
    for (let i = 0; i < MAX_POLL_ATTEMPTS; i++) {
      if (signal.aborted) return { kind: 'done', outcome: { kind: 'aborted' } };

      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
      if (signal.aborted) return { kind: 'done', outcome: { kind: 'aborted' } };

      const res = await api.e2eUploads.getE2EMediaDownload(mediaId);

      if (res.success && res.data) {
        if (recordVerdict(res.data)) return { kind: 'done', outcome: { kind: 'withheld' } };
        return { kind: 'url', downloadUrl: res.data.downloadUrl };
      }

      const code = res.error?.code;
      if (code === 'FORBIDDEN' || code === 'REJECTED' || code === 'MODERATION_ERROR') {
        return { kind: 'done', outcome: rejected(res.error) };
      }

      if (code !== 'SCAN_PENDING') {
        return {
          kind: 'done',
          outcome: { kind: 'error', message: res.error?.message ?? 'Unexpected status during moderation' },
        };
      }
    }

    return { kind: 'done', outcome: { kind: 'error', message: 'Media not available after moderation scan' } };
  }

  async function run(): Promise<DownloadOutcome> {
    try {
      let downloadUrl: string;

      const firstAttempt = await api.e2eUploads.getE2EMediaDownload(mediaId);
      if (signal.aborted) return { kind: 'aborted' };

      if (firstAttempt.success && firstAttempt.data) {
        if (recordVerdict(firstAttempt.data)) return { kind: 'withheld' };
        downloadUrl = firstAttempt.data.downloadUrl;
      } else {
        const code = firstAttempt.error?.code;
        if (code === 'SCAN_PENDING') {
          job.scanning = true;
          emit({ type: 'scanning' });
          const polled = await pollUntilAvailable();
          if (polled.kind === 'done') return polled.outcome;
          downloadUrl = polled.downloadUrl;
        } else if (code === 'FORBIDDEN' || code === 'REJECTED' || code === 'MODERATION_ERROR') {
          return rejected(firstAttempt.error);
        } else {
          return { kind: 'error', message: firstAttempt.error?.message ?? 'Failed to load media' };
        }
      }

      if (!downloadUrl) {
        return { kind: 'error', message: 'Media not available after moderation scan' };
      }

      const response = await fetch(downloadUrl, { signal });
      if (signal.aborted) return { kind: 'aborted' };
      if (!response.ok) return { kind: 'error', message: 'Failed to download media' };

      const ciphertext = new Uint8Array(await response.arrayBuffer());
      if (signal.aborted) return { kind: 'aborted' };

      const key = fromBase64(attachment.encryptionKey);
      const nonce = fromBase64(attachment.encryptionNonce);
      const plaintext = decryptSymmetric(key, ciphertext, nonce);

      const blob = new Blob([plaintext.buffer as ArrayBuffer], { type: attachment.contentType });
      return settleCached({ url: URL.createObjectURL(blob), state: 'available', bytes: blob.size });
    } catch (err) {
      if (signal.aborted) return { kind: 'aborted' };
      console.error('[useE2EMediaDownload] Error:', err);
      return { kind: 'error', message: err instanceof Error ? err.message : 'Failed to decrypt media' };
    }
  }

  job.promise = run();
  return job;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface UseE2EMediaDownloadReturn {
  state: MediaMessageState;
  imageUrl: string | null;
  rejectionReason: string | null;
  errorMessage: string | null;
  /**
   * The server reported this media was never scanned. Authoritative: it comes from the presign
   * response rather than the encrypted payload, which the sender controls.
   */
  unscanned: boolean;
  retry: () => void;
}

export function useE2EMediaDownload(
  attachment: MediaAttachment,
  options?: {
    skip?: boolean;
    /** Stop after the presign, before fetching bytes, if the media turns out to be unscanned. */
    withholdUnscanned?: boolean;
  }
): UseE2EMediaDownloadReturn {
  const { apiBaseUrl } = useAppConfig();
  const api = useMemo(() => createApiClient({ baseUrl: apiBaseUrl }), [apiBaseUrl]);
  const mediaId = attachment.e2eMediaId;
  const skip = options?.skip === true;
  const withholdUnscanned = options?.withholdUnscanned === true;

  const cached = mediaCache.get(mediaId);
  const [state, setState] = useState<MediaMessageState>(skip ? 'loading' : (cached?.state ?? 'loading'));
  const [imageUrl, setImageUrl] = useState<string | null>(skip ? null : (cached?.url || null));
  const [rejectionReason, setRejectionReason] = useState<string | null>(cached?.rejectionReason ?? null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [unscanned, setUnscanned] = useState<boolean>(unscannedVerdicts.get(mediaId) ?? false);
  const [retryCount, setRetryCount] = useState(0);

  const retry = useCallback(() => {
    // An available entry may be pinned on screen by another consumer; the rerun will reuse it.
    const entry = mediaCache.get(mediaId);
    if (entry && entry.state !== 'available') {
      removeEntry(mediaId, entry);
      notifyStats();
    }
    setRetryCount((c) => c + 1);
  }, [mediaId]);

  useEffect(() => {
    if (skip) return;

    // Cleartext Space attachments already have a CDN URL — no E2E download.
    if (attachment.cdnUrl) {
      setState('available');
      setImageUrl(attachment.cdnUrl);
      setRejectionReason(null);
      setErrorMessage(null);
      return;
    }

    let active = true;
    pinMedia(mediaId);

    const applyEntry = (entry: CachedMedia) => {
      setState(entry.state);
      setImageUrl(entry.url || null);
      setRejectionReason(entry.rejectionReason ?? null);
      setErrorMessage(null);
    };

    const hit = mediaCache.get(mediaId);
    if (hit) {
      touchEntry(mediaId, hit);
      applyEntry(hit);
      return () => {
        active = false;
        unpinMedia(mediaId);
      };
    }

    // A URL still held in state may have been revoked since (eviction, or a replaced Space logo).
    setState('loading');
    setImageUrl(null);
    setRejectionReason(null);
    setErrorMessage(null);

    const key = inflightKey(mediaId, withholdUnscanned);
    const job = joinDownloadJob(key, () =>
      startDownloadJob(api, mediaId, attachment, withholdUnscanned),
    );

    const onEvent = (event: DownloadEvent) => {
      if (!active) return;
      if (event.type === 'scanning') setState('scanning');
      else setUnscanned(event.unscanned);
    };
    job.listeners.add(onEvent);
    if (job.scanning) setState('scanning');

    void job.promise.then((outcome) => {
      if (!active) return;
      switch (outcome.kind) {
        case 'cached':
          applyEntry(outcome.entry);
          break;
        case 'error':
          setState('error');
          setErrorMessage(outcome.message);
          break;
        case 'withheld':
          setUnscanned(unscannedVerdicts.get(mediaId) ?? true);
          break;
        case 'aborted':
          break;
      }
    });

    return () => {
      active = false;
      job.listeners.delete(onEvent);
      leaveDownloadJob(key, job);
      unpinMedia(mediaId);
    };
  }, [api, mediaId, attachment.encryptionKey, attachment.encryptionNonce, attachment.contentType, attachment.cdnUrl, retryCount, skip, withholdUnscanned]);

  return { state, imageUrl, rejectionReason, errorMessage, unscanned, retry };
}
